import * as React from 'react';
import { CloudUploadOutlined, PlusOutlined } from '@ant-design/icons';
import { Button, Flex, Tooltip, Typography, Upload } from 'antd';
import { UploadChangeParam } from 'antd/es/upload';
import { UploadFile } from 'antd/lib';
import { FileWithBackendId } from '@hooks/useUpload/useUpload';
import { ATTACH_MORE, BROWSE, UPLOAD_HINT } from '@constants/chat';
import { Thumbnail } from '@components/Thumbnail/Thumbnail';
import styles from './Attachment.module.scss';

const { Paragraph } = Typography;

type Props = {
  onChange: (info: UploadChangeParam<UploadFile<File>>) => void;
  onRemoveFiles: (_: Pick<FileWithBackendId, 'id' | 'idx'>) => void;
  files: FileWithBackendId[];
  onPreview: (_: string) => void;
};

export type UploadTriggerRef = {
  trigger: () => void;
};

export const Attachment = React.forwardRef<UploadTriggerRef, Props>(
  ({ onChange, onRemoveFiles, onPreview, files }, ref) => {
    const count = React.useRef(0);
    const uploadRef = React.useRef<HTMLDivElement>(null);

    const trigger = () => {
      const upload = uploadRef.current?.querySelector('input[type="file"]') as HTMLInputElement;
      upload.click();
    };

    React.useImperativeHandle(ref, () => ({
      trigger,
    }));

    const handleChange = (info: UploadChangeParam<UploadFile<any>>) => {
      if (count.current == 0) {
        count.current = info.fileList.filter((f) => f.uid.startsWith('rc-upload')).length - 1;
        onChange(info);
      } else {
        count.current -= 1;
      }
    };

    const handlePreview = (ev: React.MouseEvent<HTMLLIElement>, id: string) => {
      ev.stopPropagation();
      onPreview(id);
    };

    return (
      <div ref={uploadRef} className={styles.attach}>
        <Upload.Dragger
          multiple
          beforeUpload={() => false}
          onChange={handleChange}
          showUploadList={false}
          className={styles.attachUpload}
          fileList={files.map(({ id: uid, file }) => ({
            url: '',
            name: file.name,
            status: file.status,
            uid,
          }))}
        >
          {!files.length && (
            <Flex vertical className={styles.attachContent}>
              <CloudUploadOutlined data-testid="cloud-icon" className={styles.attachmentIcon} />
              <Paragraph className={styles.attachHint}>
                {UPLOAD_HINT}&nbsp;
                <Button className={styles.attacBrowseButton} type="text">
                  {BROWSE}
                </Button>
              </Paragraph>
            </Flex>
          )}
          {files.length ? (
            <ul className={styles.attachUl}>
              {files.map(({ idx, ...file }) => (
                // eslint-disable-next-line jsx-a11y/no-noninteractive-element-interactions, jsx-a11y/click-events-have-key-events
                <li key={idx} onClick={(e) => handlePreview(e, file.id)}>
                  <Thumbnail onRemoveFiles={onRemoveFiles} data={{ ...file, idx }} />
                </li>
              ))}

              <Tooltip title={ATTACH_MORE}>
                <Button
                  className={styles.attachAdd}
                  data-testid="attachFiles"
                  icon={<PlusOutlined style={{ fontSize: '24px' }} />}
                  onClick={() => {
                    uploadRef.current?.click();
                  }}
                />
              </Tooltip>
            </ul>
          ) : null}
        </Upload.Dragger>
      </div>
    );
  },
);

Attachment.displayName = 'Attachment';
